/** Task & Action Management — React Query hooks over the isolated store. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/store/toast'
import { actionsStore } from './store'
import type { ActionTask } from './types'

const KEY = ['action-management']

export function useActions() {
  return useQuery({ queryKey: [...KEY, 'list'], queryFn: () => actionsStore.all() })
}

export function useAction(id: string) {
  return useQuery({ queryKey: [...KEY, 'item', id], queryFn: () => actionsStore.get(id), enabled: !!id })
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEY })
}

export function useCreateAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<ActionTask>) => actionsStore.create(payload),
    onSuccess: (t) => {
      invalidate(qc)
      toast.success('Task created', `${t.number} added to the action list.`)
    },
    onError: (e) => toast.error('Could not save', (e as Error).message),
  })
}

export function useUpdateAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ActionTask> }) => actionsStore.update(id, patch),
    onSuccess: (t) => {
      invalidate(qc)
      qc.invalidateQueries({ queryKey: [...KEY, 'item', t.id] })
    },
    onError: (e) => toast.error('Update failed', (e as Error).message),
  })
}

export function useDeleteAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => actionsStore.remove(id),
    onSuccess: () => {
      invalidate(qc)
      toast.success('Task removed')
    },
    onError: (e) => toast.error('Delete failed', (e as Error).message),
  })
}
